import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    Gamepad2,
    MessageCircle,
    Cake,
    CalendarClock,
    Star,
    PenLine,
    Hash,
    Send,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    ShieldCheck,
    LockKeyhole,
} from "lucide-react";
import PageShell from "../components/PageShell";
import PageHeader from "../components/PageHeader";

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS_BY_ID = {
    roblox_nick: Gamepad2,
    discord_user: MessageCircle,
    idade: Cake,
    disponibilidade: CalendarClock,
    experiencia: Star,
    motivacao: PenLine,
};
const ICONS_BY_TYPE = { text: PenLine, number: Hash, textarea: PenLine };

function formatError(detail) {
    if (detail == null) return "Não foi possível enviar. Tente novamente em alguns instantes.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && e.msg ? e.msg : String(e))).join(" ");
    return String(detail);
}

export default function Formulario() {
    const { formId } = useParams();
    const id = formId || "staff-application";
    const [form, setForm] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [answers, setAnswers] = useState({});
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const load = useCallback(() => {
        axios
            .get(`${API}/api/forms/${id}`)
            .then((res) => setForm(res.data))
            .catch(() => setNotFound(true));
    }, [id]);

    useEffect(() => {
        setForm(null);
        setNotFound(false);
        setAnswers({});
        setStatus("idle");
        load();
    }, [load]);

    const questions = (form?.questions || []).filter((q) => q.enabled);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        const missing = questions.find((q) => q.required && !String(answers[q.id] || "").trim());
        if (missing) {
            setErrorMsg(`Preencha o campo "${missing.label}" antes de enviar.`);
            return;
        }
        setStatus("loading");
        try {
            await axios.post(`${API}/api/forms/${id}/submit`, { answers });
            setStatus("success");
        } catch (err) {
            if (err.response?.status === 403) {
                setForm((f) => ({ ...f, open: false }));
                setStatus("idle");
                return;
            }
            setStatus("idle");
            setErrorMsg(formatError(err.response?.data?.detail));
        }
    };

    if (notFound) {
        return (
            <PageShell testid="page-formulario">
                <div className="glass-card mx-auto mt-16 flex max-w-md flex-col items-center gap-3 rounded-2xl px-6 py-14 text-center">
                    <AlertTriangle className="h-8 w-8 text-gray-600" />
                    <p className="font-display text-lg font-bold text-gray-300">Formulário não encontrado</p>
                    <p className="text-sm text-gray-500">Verifique o link ou fale com a administração.</p>
                </div>
            </PageShell>
        );
    }

    if (!form) {
        return (
            <PageShell testid="page-formulario">
                <div className="flex items-center justify-center py-24 text-gray-500">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell testid="page-formulario">
            <PageHeader
                eyebrow="Formulário"
                title={form.name}
                description="Preencha o formulário abaixo com atenção. Sua resposta é enviada diretamente para o Discord da administração da EQUIPE STAFF [EBH1]."
            />
            <div className="mx-auto max-w-2xl">
                {!form.open ? (
                    <div
                        data-testid="form-closed-message"
                        className="glass-card flex flex-col items-center gap-4 rounded-2xl px-6 py-14 text-center"
                    >
                        <span className="flex h-16 w-16 items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/10">
                            <LockKeyhole className="h-8 w-8 text-amber-400" />
                        </span>
                        <h2 className="font-display text-2xl font-bold text-white">Inscrições encerradas</h2>
                        <p className="max-w-md text-sm leading-relaxed text-gray-400">
                            Este formulário está fechado no momento. As inscrições serão
                            reabertas em breve — acompanhe os avisos no Discord oficial
                            da EQUIPE STAFF [EBH1].
                        </p>
                    </div>
                ) : status === "success" ? (
                    <div
                        data-testid="form-success-message"
                        className="glass-card flex flex-col items-center gap-4 rounded-2xl px-6 py-14 text-center"
                    >
                        <span className="flex h-16 w-16 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10">
                            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                        </span>
                        <h2 className="font-display text-2xl font-bold text-white">Resposta enviada!</h2>
                        <p className="max-w-md text-sm leading-relaxed text-gray-400">
                            Sua resposta foi recebida pela administração. Aguarde a
                            análise no Discord — se aprovado, um membro da gerência
                            entrará em contato.
                        </p>
                        <button
                            data-testid="form-send-another-button"
                            onClick={() => {
                                setAnswers({});
                                setStatus("idle");
                            }}
                            className="mt-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-6 py-2.5 text-sm font-bold text-violet-300 transition-colors hover:bg-violet-500/20"
                        >
                            Enviar outra resposta
                        </button>
                    </div>
                ) : (
                    <form
                        data-testid="staff-application-form"
                        onSubmit={handleSubmit}
                        className="glass-card rounded-2xl p-6 sm:p-8"
                    >
                        <div className="space-y-5">
                            {questions.map((q) => {
                                const Icon = ICONS_BY_ID[q.id] || ICONS_BY_TYPE[q.type] || PenLine;
                                return (
                                    <div key={q.id}>
                                        <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-gray-400">
                                            <Icon className="h-3.5 w-3.5 text-violet-400" />
                                            {q.label}
                                            {!q.required && (
                                                <span className="text-[10px] font-medium normal-case tracking-normal text-gray-600">
                                                    (opcional)
                                                </span>
                                            )}
                                        </label>
                                        {q.type === "textarea" ? (
                                            <textarea
                                                data-testid={`form-field-${q.id}`}
                                                rows={4}
                                                value={answers[q.id] || ""}
                                                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                                                placeholder={q.placeholder}
                                                className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-100 outline-none transition-colors placeholder:text-gray-600 focus:border-violet-500/60 focus:bg-violet-500/5"
                                            />
                                        ) : (
                                            <input
                                                data-testid={`form-field-${q.id}`}
                                                type={q.type === "number" ? "number" : "text"}
                                                value={answers[q.id] || ""}
                                                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                                                placeholder={q.placeholder}
                                                min={q.type === "number" ? 1 : undefined}
                                                max={q.type === "number" ? 120 : undefined}
                                                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-gray-100 outline-none transition-colors placeholder:text-gray-600 focus:border-violet-500/60 focus:bg-violet-500/5"
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {errorMsg && (
                            <div
                                data-testid="form-error-message"
                                className="mt-5 flex items-center gap-2.5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300"
                            >
                                <AlertTriangle className="h-4 w-4 shrink-0" />
                                {errorMsg}
                            </div>
                        )}
                        <button
                            data-testid="form-submit-button"
                            type="submit"
                            disabled={status === "loading"}
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-violet-900/40 transition-all duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {status === "loading" ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" /> Enviar
                                </>
                            )}
                        </button>
                        <p className="mt-4 flex items-center justify-center gap-2 text-center text-[11px] text-gray-500">
                            <ShieldCheck className="h-3.5 w-3.5 text-violet-400" />
                            Envio seguro direto para o canal da administração no Discord
                        </p>
                    </form>
                )}
            </div>
        </PageShell>
    );
}
